import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from './firebase';
import RegistrationWizard from './RegistrationWizard';
import appstoreIcon from './assets/appstore_icon.svg';
import playstoreIcon from './assets/playstore_icon.svg';

function Home() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, message: '' });
  const [igLoaded, setIgLoaded] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      
      // If user is already logged in and not a new signup, redirect to profile
      if (currentUser && !isNewUser) {
        navigate('/profile');
      }
    });

    return () => unsubscribe();
  }, [navigate, isNewUser]);

  const validatePasswordStrength = (pass) => {
    let score = 0;
    let message = '';
    
    if (pass.length >= 8) score++;
    if (pass.length >= 12) score++;
    if (/[a-z]/.test(pass) && /[A-Z]/.test(pass)) score++;
    if (/\d/.test(pass)) score++;
    if (/[^a-zA-Z\d]/.test(pass)) score++;
    
    if (score < 3) message = 'Weak - Add uppercase, numbers, and symbols';
    else if (score < 4) message = 'Medium - Add more complexity';
    else message = 'Strong password';
    
    return { score, message };
  };

  const handlePasswordChange = (pass) => {
    setPassword(pass);
    if (authMode === 'signup') {
      setPasswordStrength(validatePasswordStrength(pass));
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setLoading(true);
    
    try {
      const strength = validatePasswordStrength(password);
      if (strength.score < 3) {
        setAuthError('Password is too weak. Must be at least 8 characters with uppercase, lowercase, numbers, and symbols.');
        setLoading(false);
        return;
      }
      
      if (password !== confirmPassword) {
        setAuthError('Passwords do not match');
        setLoading(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      
      setAuthSuccess('Account created! Please check your email to verify your account.');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setIsNewUser(true);
      
      setTimeout(() => {
        setShowAuthModal(false);
        setAuthSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Error signing up:', error);
      if (error.code === 'auth/email-already-in-use') {
        setAuthError('Email already in use. Please sign in instead.');
      } else if (error.code === 'auth/invalid-email') {
        setAuthError('Invalid email address.');
      } else if (error.code === 'auth/weak-password') {
        setAuthError('Password is too weak.');
      } else {
        setAuthError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setLoading(true);
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setShowAuthModal(false);
      setEmail('');
      setPassword('');
    } catch (error) {
      console.error('Error signing in:', error);
      if (error.code === 'auth/user-not-found') {
        setAuthError('No account found with this email.');
      } else if (error.code === 'auth/wrong-password') {
        setAuthError('Incorrect password.');
      } else if (error.code === 'auth/invalid-email') {
        setAuthError('Invalid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setAuthError('Too many failed attempts. Please try again later.');
      } else {
        setAuthError('Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setLoading(true);
    
    try {
      await sendPasswordResetEmail(auth, email);
      setAuthSuccess('Password reset email sent! Check your inbox.');
      setEmail('');
      
      setTimeout(() => {
        setAuthMode('signin');
        setAuthSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Error resetting password:', error);
      if (error.code === 'auth/user-not-found') {
        setAuthError('No account found with this email.');
      } else if (error.code === 'auth/invalid-email') {
        setAuthError('Invalid email address.');
      } else {
        setAuthError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    setShowAuthModal(true);
    setAuthMode('signin');
    setAuthError('');
    setAuthSuccess('');
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setShowWizard(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    // Only show wizard for new users who just signed up
    if (user && isNewUser && !showWizard) {
      setShowWizard(true);
    } else if (!user) {
      setShowWizard(false);
    }
  }, [user, showWizard, isNewUser]);

  useEffect(() => {
    // Load Instagram embed script after component mounts
    if (!igLoaded && !loading) {
      const script = document.createElement('script');
      script.src = '//www.instagram.com/embed.js';
      script.async = true;
      script.onload = () => {
        setIgLoaded(true);
        if (window.instgrm) {
          window.instgrm.Embeds.process();
        }
      };
      document.body.appendChild(script);
    }
  }, [loading, igLoaded]);

  const handleWizardComplete = () => {
    setShowWizard(false);
    navigate('/profile');
  };

  if (loading) {
    return (
      <>
        <style>{`
          .page-container {
            min-height: 100vh;
            background: #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .loading-text {
            font-size: 18px;
            color: #6b7280;
            font-weight: 500;
          }
        `}</style>
        <div className="page-container">
          <div className="loading-text">Loading...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }

        .page-wrapper {
          width: 100%;
          min-height: 100vh;
          background: #ffffff;
        }

        /* Hero Section */
        .hero {
          position: relative;
          height: auto;
          min-height: 600px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
          padding: 80px 20px 80px;
          overflow: hidden;
        }

        .hero-pattern-border {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 80px;
          background: 
            repeating-linear-gradient(135deg, #C60000 0px, #C60000 1px, transparent 1px, transparent 10px, #C60000 10px, #C60000 11px, transparent 11px, transparent 20px),
            repeating-linear-gradient(45deg, transparent 0px, transparent 10px, #C60000 10px, #C60000 11px, transparent 11px, transparent 20px);
          background-size: 20px 20px, 20px 20px;
          opacity: 0.18;
          z-index: 0;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 30%, #C60000 1.5px, transparent 1.5px),
            radial-gradient(circle at 45% 60%, #C60000 1.5px, transparent 1.5px),
            radial-gradient(circle at 70% 40%, #C60000 1.5px, transparent 1.5px),
            radial-gradient(circle at 85% 70%, #C60000 1.5px, transparent 1.5px),
            radial-gradient(circle at 15% 80%, #C60000 1.5px, transparent 1.5px);
          background-size: 100% 100%;
          opacity: 0.03;
          pointer-events: none;
          z-index: 0;
        }

        .hero::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 90%;
          max-width: 1000px;
          height: 70%;
          background: 
            repeating-linear-gradient(135deg, transparent 0px, transparent 9px, #C60000 9px, #C60000 10px, transparent 10px, transparent 19px, #C60000 19px, #C60000 20px),
            repeating-linear-gradient(45deg, transparent 0px, transparent 9px, #C60000 9px, #C60000 10px, transparent 10px, transparent 19px, #C60000 19px, #C60000 20px);
          background-size: 28px 28px;
          opacity: 0.04;
          pointer-events: none;
          z-index: 0;
        }

        .hero-content {
          position: relative;
          z-index: 1;
          text-align: center;
          max-width: 900px;
          width: 100%;
          margin: 0 auto;
        }

        .logo-header {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 35px;
        }

        .brand-name {
          font-size: 64px;
          font-weight: 900;
          color: #C60000;
          letter-spacing: -2px;
          line-height: 1.1;
        }

        .tagline {
          font-size: 22px;
          color: #4b5563;
          margin-bottom: 40px;
          font-weight: 500;
        }

        .cta-group {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 30px;
          width: 100%;
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .options-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 25px;
          width: 100%;
          margin: 0 auto;
        }

        .option-panel {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 32px 28px;
          text-align: left;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          display: flex;
          flex-direction: column;
        }

        .option-panel:hover {
          border-color: #C60000;
          box-shadow: 0 8px 24px rgba(198, 0, 0, 0.12);
          transform: translateY(-2px);
        }

        .panel-title {
          font-size: 24px;
          font-weight: 800;
          color: #C60000;
          margin: 0 0 16px 0;
          letter-spacing: -0.5px;
        }

        .panel-description {
          font-size: 15px;
          color: #374151;
          line-height: 1.6;
          margin: 0 0 16px 0;
        }

        .panel-highlight {
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
          margin: 0 0 24px 0;
          padding: 12px 16px;
          background: #fef2f2;
          border-radius: 8px;
          border-left: 3px solid #C60000;
        }

        .btn-primary {
          background: linear-gradient(135deg, #C60000 0%, #a50000 100%);
          color: white;
          border: none;
          padding: 18px 50px;
          font-size: 18px;
          font-weight: 700;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 8px 24px rgba(198, 0, 0, 0.3);
          letter-spacing: 0.3px;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(198, 0, 0, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .app-buttons {
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
          width: 100%;
        }

        .app-link {
          display: block;
          transition: transform 0.3s ease;
        }

        .app-link:hover {
          transform: scale(1.05);
        }

        .app-badge {
          height: 45px;
          width: 150px;
          object-fit: contain;
        }

        /* Instagram Section */
        .testimonials {
          position: relative;
          padding: 60px 20px 100px;
          background: #fafafa;
          overflow: hidden;
        }

        .testimonials::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 60px;
          background: 
            repeating-linear-gradient(135deg, #C60000 0px, #C60000 1px, transparent 1px, transparent 10px, #C60000 10px, #C60000 11px, transparent 11px, transparent 20px),
            repeating-linear-gradient(45deg, transparent 0px, transparent 10px, #C60000 10px, #C60000 11px, transparent 11px, transparent 20px);
          background-size: 20px 20px, 20px 20px;
          opacity: 0.18;
        }
        
        .testimonials::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 60px;
          background: 
            repeating-linear-gradient(135deg, #C60000 0px, #C60000 1px, transparent 1px, transparent 10px, #C60000 10px, #C60000 11px, transparent 11px, transparent 20px),
            repeating-linear-gradient(45deg, transparent 0px, transparent 10px, #C60000 10px, #C60000 11px, transparent 11px, transparent 20px);
          background-size: 20px 20px, 20px 20px;
          opacity: 0.18;
        }

        .testimonials-content {
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .section-title {
          font-size: 40px;
          font-weight: 800;
          text-align: center;
          color: #1a1a1a;
          margin-bottom: 50px;
        }

        .ig-embed-wrapper {
          background: white;
          border-radius: 3px;
          padding: 0;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        /* Footer */
        .footer {
          background: #ffffff;
          padding: 40px 20px;
          text-align: center;
          border-top: 1px solid #e5e7eb;
        }

        .footer-links {
          display: flex;
          justify-content: center;
          gap: 30px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .footer-link {
          color: #4b5563;
          text-decoration: none;
          font-size: 15px;
          font-weight: 500;
          transition: color 0.2s ease;
        }

        .footer-link:hover {
          color: #C60000;
        }

        .copyright {
          color: #6b7280;
          font-size: 14px;
        }

        /* User Section */
        .user-section {
          padding: 30px 20px;
          background: white;
          text-align: center;
        }

        .user-card {
          max-width: 500px;
          margin: 0 auto;
          background: #fafafa;
          padding: 30px;
          border-radius: 16px;
          border: 1px solid #e5e7eb;
        }

        .user-greeting {
          color: #1a1a1a;
          font-size: 18px;
          margin-bottom: 20px;
          font-weight: 600;
        }

        .btn-secondary {
          background: #6b7280;
          color: white;
          border: none;
          padding: 12px 32px;
          font-size: 16px;
          border-radius: 50px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover {
          background: #4b5563;
          transform: translateY(-2px);
        }

        /* Authentication Modal */
        .auth-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .auth-modal {
          background: white;
          border-radius: 16px;
          padding: 40px;
          max-width: 450px;
          width: 100%;
          position: relative;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .close-modal {
          position: absolute;
          top: 15px;
          right: 15px;
          background: none;
          border: none;
          font-size: 32px;
          color: #6b7280;
          cursor: pointer;
          line-height: 1;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s;
        }

        .close-modal:hover {
          color: #1a1a1a;
        }

        .auth-title {
          font-size: 28px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 24px 0;
          text-align: center;
        }

        .auth-error {
          background: #fee;
          color: #c00;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 14px;
          border-left: 3px solid #c00;
        }

        .auth-success {
          background: #efe;
          color: #060;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 14px;
          border-left: 3px solid #060;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.2s;
          box-sizing: border-box;
        }

        .form-input:focus {
          outline: none;
          border-color: #C60000;
        }

        .password-strength {
          margin-top: 8px;
          font-size: 13px;
          font-weight: 500;
          padding: 6px 10px;
          border-radius: 4px;
        }

        .strength-0, .strength-1, .strength-2 {
          background: #fee;
          color: #c00;
        }

        .strength-3 {
          background: #fef3cd;
          color: #856404;
        }

        .strength-4, .strength-5 {
          background: #d4edda;
          color: #155724;
        }

        .btn-auth {
          width: 100%;
          background: #C60000;
          color: white;
          border: none;
          padding: 14px;
          font-size: 16px;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s;
          margin-top: 8px;
        }

        .btn-auth:hover:not(:disabled) {
          background: #a00000;
        }

        .btn-auth:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .auth-footer {
          margin-top: 24px;
          text-align: center;
        }

        .link-button {
          background: none;
          border: none;
          color: #C60000;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          padding: 0;
          text-decoration: underline;
        }

        .link-button:hover {
          color: #a00000;
        }

        .auth-switch {
          margin-top: 16px;
          font-size: 14px;
          color: #6b7280;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .hero {
            min-height: auto;
            padding: 60px 20px 60px;
          }

          .hero::before,
          .hero::after {
            width: 300px;
            height: 300px;
          }

          .hero-content {
            padding: 0;
          }

          .logo-header {
            margin-bottom: 25px;
          }

          .brand-name {
            font-size: 52px;
          }

          .tagline {
            font-size: 18px;
            margin-bottom: 30px;
            padding: 0 10px;
          }

          .btn-primary {
            padding: 16px 40px;
            font-size: 16px;
            width: 100%;
          }

          .options-container {
            grid-template-columns: 1fr;
            gap: 20px;
            width: 100%;
          }

          .option-panel {
            padding: 28px 24px;
          }

          .panel-title {
            font-size: 20px;
          }

          .panel-description {
            font-size: 14px;
          }

          .panel-highlight {
            font-size: 13px;
            margin-bottom: 20px;
          }

          .app-badge {
            height: 50px;
            width: 160px;
          }

          .testimonials {
            padding: 40px 15px 70px;
          }

          .section-title {
            font-size: 32px;
            margin-bottom: 40px;
          }

          .testimonials-content {
            max-width: 100%;
            padding: 0 10px;
          }

          .footer-links {
            gap: 20px;
          }
        }

        @media (max-width: 480px) {
          .hero {
            padding: 40px 15px 50px;
          }

          .logo-header {
            margin-bottom: 20px;
          }

          .brand-name {
            font-size: 48px;
          }

          .tagline {
            font-size: 16px;
            padding: 0 5px;
          }

          .cta-group {
            gap: 20px;
          }

          .options-container {
            gap: 16px;
          }

          .option-panel {
            padding: 24px 20px;
          }

          .section-title {
            font-size: 28px;
          }

          .panel-title {
            font-size: 18px;
            margin-bottom: 12px;
          }

          .panel-description {
            font-size: 13px;
            margin-bottom: 12px;
          }

          .panel-highlight {
            font-size: 12px;
            padding: 10px 12px;
            margin-bottom: 16px;
          }

          .btn-primary {
            padding: 14px 32px;
            font-size: 15px;
          }

          .app-badge {
            width: 150px;
            height: 48px;
          }
        }
      `}</style>

      <div className="page-wrapper">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-pattern-border"></div>
          <div className="hero-content">
            <div className="logo-header">
              <h1 className="brand-name">Jebena</h1>
            </div>
            <p className="tagline">
              The #1 dating app for Ethiopians and Eritreans. Over 1 million matches and counting.
            </p>
            <div className="cta-group">
              <div className="options-container">
                {/* Web Matchmaking Panel */}
                <div className="option-panel">
                  <h3 className="panel-title">Web</h3>
                  <p className="panel-description">
                    Personalized matchmaking platform for meaningful connections
                  </p>
                  <p className="panel-highlight">
                    In-person events like "Pitch-Your-Friend" at local coffee shops
                  </p>
                  {!user && (
                    <button 
                      onClick={handleGetStarted} 
                      className="btn-primary"
                      disabled={loading}
                    >
                      Get Started
                    </button>
                  )}
                </div>

                {/* Mobile Apps Panel */}
                <div className="option-panel">
                  <h3 className="panel-title">Download Mobile Apps</h3>
                  <p className="panel-description">
                    Full experience with messaging, profiles, and real-time matching
                  </p>
                  <div className="app-buttons">
                    <a 
                      href="https://apps.apple.com/us/app/id1497800659"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-link"
                    >
                      <img 
                        src={appstoreIcon} 
                        alt="Download on App Store"
                        className="app-badge"
                      />
                    </a>
                    <a 
                      href="https://play.google.com/store/apps/details?id=app.jebena.jb"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-link"
                    >
                      <img 
                        src={playstoreIcon} 
                        alt="Get it on Google Play"
                        className="app-badge"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Instagram Testimonials Section */}
        <section className="testimonials">
          <div className="testimonials-content">
            <h2 className="section-title">Success Stories and More</h2>
            <div className="ig-embed-wrapper">
              <blockquote 
                className="instagram-media" 
                data-instgrm-permalink="https://www.instagram.com/jebena_app/"
                data-instgrm-version="14"
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 0,
                  boxShadow: 'none',
                  margin: '0 auto',
                  maxWidth: '100%',
                  minWidth: '100%',
                  padding: 0,
                  width: '100%',
                  height: '600px'
                }}
              >
                <div style={{ padding: '16px' }}>
                  <a 
                    href="https://www.instagram.com/jebena_app/" 
                    style={{ 
                      background: '#FFFFFF',
                      lineHeight: 0,
                      padding: '0 0',
                      textAlign: 'center',
                      textDecoration: 'none',
                      width: '100%'
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View this profile on Instagram
                  </a>
                </div>
              </blockquote>
            </div>
          </div>
        </section>

        {/* User Section (if logged in) */}
        {user && (
          <section className="user-section">
            <div className="user-card">
              <p className="user-greeting">Welcome, {user.displayName || user.email}!</p>
              <button onClick={handleSignOut} className="btn-secondary">
                Sign Out
              </button>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="footer">
          <div className="footer-links">
            <a href="/" className="footer-link">Home</a>
            <a href="/privacy" className="footer-link">Privacy</a>
            <a href="/terms" className="footer-link">Terms</a>
          </div>
          <p className="copyright">
            © {new Date().getFullYear()} Jebena Tech, Inc.
          </p>
        </footer>

        {/* Authentication Modal */}
        {showAuthModal && (
          <div className="auth-modal-overlay" onClick={() => setShowAuthModal(false)}>
            <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
              <button className="close-modal" onClick={() => setShowAuthModal(false)}>×</button>
              
              <h2 className="auth-title">
                {authMode === 'signin' && 'Sign In'}
                {authMode === 'signup' && 'Create Account'}
                {authMode === 'reset' && 'Reset Password'}
              </h2>

              {authError && <div className="auth-error">{authError}</div>}
              {authSuccess && <div className="auth-success">{authSuccess}</div>}

              <form onSubmit={
                authMode === 'signin' ? handleSignIn :
                authMode === 'signup' ? handleSignUp :
                handlePasswordReset
              }>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="your@email.com"
                    className="form-input"
                  />
                </div>

                {authMode !== 'reset' && (
                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => handlePasswordChange(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="form-input"
                    />
                    {authMode === 'signup' && password && (
                      <div className={`password-strength strength-${passwordStrength.score}`}>
                        {passwordStrength.message}
                      </div>
                    )}
                  </div>
                )}

                {authMode === 'signup' && (
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="form-input"
                    />
                  </div>
                )}

                <button type="submit" className="btn-auth" disabled={loading}>
                  {loading ? 'Processing...' : 
                    authMode === 'signin' ? 'Sign In' :
                    authMode === 'signup' ? 'Create Account' :
                    'Send Reset Email'
                  }
                </button>
              </form>

              <div className="auth-footer">
                {authMode === 'signin' && (
                  <>
                    <button className="link-button" onClick={() => setAuthMode('reset')}>
                      Forgot password?
                    </button>
                    <p className="auth-switch">
                      Don't have an account?{' '}
                      <button className="link-button" onClick={() => setAuthMode('signup')}>
                        Sign up
                      </button>
                    </p>
                  </>
                )}
                {authMode === 'signup' && (
                  <p className="auth-switch">
                    Already have an account?{' '}
                    <button className="link-button" onClick={() => setAuthMode('signin')}>
                      Sign in
                    </button>
                  </p>
                )}
                {authMode === 'reset' && (
                  <button className="link-button" onClick={() => setAuthMode('signin')}>
                    Back to sign in
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Registration Wizard Modal */}
        {showWizard && user && (
          <RegistrationWizard 
            user={user} 
            onComplete={handleWizardComplete}
          />
        )}
      </div>
    </>
  );
}

export default Home;
